import {
  Box,
  Checkbox,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  TextField,
  Typography,
} from '@mui/material'

import { FixedSizeList } from 'react-window'
import { Icon as Iconify } from '@iconify/react'
import React from 'react'
import SearchIcon from '@mui/icons-material/Search'
import { appColors } from 'utils'
import useInnovaTheme from 'components/common//hooks/useInnovaTheme'

const VIRTUALIZE_THRESHOLD = 200

function containsText(text, query) {
  return String(text).toLowerCase().indexOf(String(query).toLowerCase()) > -1
}

function renderIcon(icon) {
  if (!icon) return null

  if (typeof icon === 'string') {
    return <Iconify icon={icon} width={18} height={18} />
  }

  if (React.isValidElement(icon)) {
    return icon
  }

  if (typeof icon === 'function') {
    const IconComponent = icon
    return <IconComponent size={18} />
  }

  return null
}

function useColors(fontStyle, getBackColor, getTextColor) {
  const { fontSize, fontColor, backColor } = fontStyle || {}
  const bg = backColor || getBackColor()
  const fg = fontColor || getTextColor()
  return { fontSize: fontSize || 16, labelSize: fontStyle?.fontSize || 14, fontColor: fg, backColor: bg }
}

function MenuPaperStyles(backColor, fontSize) {
  return {
    sx: {
      maxHeight: 450,
      borderRadius: 0,
      backgroundColor: backColor,
      '& ul': { backgroundColor: backColor },
      '& li': { fontSize: fontSize || 12 },
      scrollbarWidth: 'thin',
      scrollbarColor: '#A0A0A0 transparent',
    },
  }
}

export function DropDownPicker({
  value,
  defaultValue,
  displayEmpty,
  listOptions,
  onChange,
  label = null,
  height,
  fontStyle,
  width = '100%',
  maxWidth = width === '100%' ? '250px' : width,
  disabled = false,
}) {
  const { getBackColor, getTextColor, theme } = useInnovaTheme()
  const { fontColor, labelSize, fontSize, backColor } = useColors(fontStyle, getBackColor, getTextColor)

  const finalHeight = height || 40

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: width }}>
        {label !== null ? (
          <InputLabel
            id='dropdown-picker-label'
            sx={{
              color: fontColor,
              fontSize: labelSize,
              transition:
                'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, top 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',

              '&.Mui-focused': {
                color: theme === 'dark' ? null : fontColor,
                textShadow: theme === 'dark' ? null : `0 0 5px #fff`,
              },
              '&.MuiInputLabel-shrink': {
                top: 0,
              },
              '&:not(.MuiInputLabel-shrink)': {
                top: '50%',
                transform: 'translate(14px, -50%)',
              },
            }}>
            {label}
          </InputLabel>
        ) : null}

        <MuiSelect
          id='dropdown-picker-select'
          labelId='dropdown-picker-label'
          label={label}
          disabled={disabled}
          value={value != null && listOptions?.length > 0 ? value : ''}
          defaultValue={defaultValue}
          displayEmpty={displayEmpty}
          onChange={(e) => onChange?.(e.target.value)}
          variant='outlined'
          MenuProps={{ PaperProps: MenuPaperStyles(backColor, fontSize) }}
          renderValue={(selected) => {
            if (!selected && displayEmpty) return <span style={{ color: fontColor, opacity: 0.8 }}>—</span>

            const opt = Array.isArray(listOptions)
              ? listOptions.find((o) => (typeof o === 'object' ? o.value : o) === selected)
              : null

            const labelText = typeof opt === 'object' ? opt.label : selected
            const iconNode = typeof opt === 'object' ? renderIcon(opt.icon) : null
            const highlight = typeof opt === 'object' ? opt.highlight : null

            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%', overflow: 'hidden' }}>
                {highlight && (
                  <Box
                    sx={{
                      width: 4,
                      height: 18,
                      borderRadius: 1,
                      backgroundColor: highlight,
                    }}
                  />
                )}

                {iconNode}
                <Box
                  component='span'
                  sx={{
                    lineHeight: 1.2,
                    color: fontColor,
                    fontSize,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {labelText}
                </Box>
              </Box>
            )
          }}
          style={{
            width,
            maxWidth,
            height: finalHeight,
            color: fontColor,
            backgroundColor: backColor,
          }}
          sx={{
            width,
            maxWidth,
            height: finalHeight,
            color: fontColor,
            backgroundColor: backColor,
            '& .MuiSelect-select': {
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              height: '100% !important',
              minHeight: 'auto !important',
              display: 'flex !important',
              alignItems: 'center',
              paddingLeft: '14px',
            },
            '& .MuiSelect-select.Mui-disabled': {
              color: `${fontColor} !important`,
              WebkitTextFillColor: `${fontColor} !important`,
              opacity: '.8 !important',
            },
            '&.Mui-disabled .MuiSelect-select': {
              color: `${fontColor} !important`,
              WebkitTextFillColor: `${fontColor} !important`,
              opacity: '.8 !important',
            },
            '& .MuiSelect-icon': {
              color: fontColor,
              top: '50%',
              transform: 'translateY(-50%)',
              '&.Mui-select-iconOpen': {
                transform: 'translateY(-50%) rotate(180deg)',
              },
            },

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#429CEB',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#429CEB',
              boxShadow: '0 0 6px rgba(66,156,235,0.3)',
            },
          }}>
          {Array.isArray(listOptions)
            ? listOptions.map((item) => {
                const val = typeof item === 'object' ? item.value : item
                const lbl = typeof item === 'object' ? item.label : item
                const icon = typeof item === 'object' ? renderIcon(item.icon) : null
                const highlight = typeof item === 'object' ? item.highlight : null
                return (
                  <MenuItem
                    key={String(val)}
                    value={val}
                    dense
                    sx={{
                      height: 32,
                      mx: 0.5,
                      my: 0.25,
                      bgcolor: backColor,
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}>
                      {highlight && (
                        <Box
                          sx={{
                            width: 4,
                            height: 18,
                            borderRadius: 1,
                            backgroundColor: highlight,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {icon && <ListItemIcon sx={{ minWidth: 22 }}>{icon}</ListItemIcon>}
                      <ListItemText
                        primary={lbl}
                        primaryTypographyProps={{
                          fontSize,
                          color: fontColor,
                        }}
                      />
                    </Box>
                  </MenuItem>
                )
              })
            : null}
        </MuiSelect>
      </FormControl>
    </React.Fragment>
  )
}

// listOptions must be an array of strings
// non-virtualized contains a select all feature
export function DropDownMultiselect(props) {
  const { listOptions = [] } = props || {}
  if (Array.isArray(listOptions) && listOptions.length > VIRTUALIZE_THRESHOLD) {
    return <DropDownMultiselectVirtualized {...props} />
  }
  return <DropDownMultiselectSmall {...props} />
}

function DropDownMultiselectSmall({
  selected = [],
  defaultValue,
  displayEmpty,
  listOptions = [],
  onClose,
  onChange,
  label = null,
  height = 40,
  fontStyle,
  width = '250px',
  maxWidth = '250px',
  disabled = false,
}) {
  const { theme, getBackColor, getTextColor } = useInnovaTheme()
  const { fontColor, labelSize, fontSize, backColor } = useColors(fontStyle, getBackColor, getTextColor)
  const isAllSelected = selected.length > 0 && selected.length === listOptions.length

  const [searchText, setSearchText] = React.useState('')
  const displayedOptions = React.useMemo(() => {
    if (!Array.isArray(listOptions)) return []
    if (!searchText) return listOptions
    return listOptions.filter((opt) => containsText(opt, searchText))
  }, [listOptions, searchText])

  const handleChange = (event) => {
    const value = event.target.value
    if (value[value.length - 1] === 'all') {
      onChange(selected.length === listOptions.length ? [] : listOptions)
      return
    }
    onChange(value)
  }

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: width }} size={height < 40 ? 'small' : 'medium'}>
        <InputLabel
          id='dropdown-picker-label'
          sx={{
            color: fontColor,
            fontSize: labelSize,
            '&.Mui-focused': {
              color: theme === 'dark' ? null : fontColor,
              textShadow: theme === 'dark' ? null : `0 0 5px #fff`,
            },
          }}>
          {label}
        </InputLabel>

        <MuiSelect
          id='dropdown-picker-multiselect'
          labelId={`dropdown-picker-multiselect-label`}
          label={label}
          disabled={disabled}
          value={selected || ''}
          defaultValue={defaultValue}
          displayEmpty={displayEmpty}
          multiple
          renderValue={(vals) => (Array.isArray(vals) ? vals.join(', ') : '')}
          onClose={(e) => {
            setSearchText('')
            onClose && onClose(e)
          }}
          onChange={handleChange}
          style={{
            width,
            maxWidth,
            margin: 0,
            borderRadius: 0,
            color: fontColor,
            fontSize,
            border: '',
            height: `${height}px`,
            boxSizing: 'border-box',
            backgroundColor: backColor,
          }}
          sx={{ '& .MuiSelect-select': { color: fontColor }, svg: { color: fontColor } }}
          MenuProps={{
            autoFocus: false,
            MenuListProps: { disablePadding: true },
            PaperProps: MenuPaperStyles(backColor, fontSize),
          }}
          variant='outlined'>
          <ListSubheader disableSticky sx={{ backgroundColor: backColor }}>
            <TextField
              variant='outlined'
              size='small'
              autoFocus
              placeholder='Type to search...'
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') e.stopPropagation()
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon sx={{ color: fontColor }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                fontSize: fontSize || 12,
                '& .MuiInputBase-root': { backgroundColor: backColor, color: fontColor, fontSize: fontSize || 12 },
              }}
            />
          </ListSubheader>

          <MenuItem
            id='dropdown-picker-multiselect-all'
            value={'all'}
            key='dropdown-picker-multiselect-all'
            style={{
              margin: 5,
              fontSize,
              paddingVertical: 2,
              paddingHorizontal: 10,
              borderWidth: 1,
              height: '32px',
              color: fontColor || appColors.itemTextColor,
              borderColor: appColors.borderColor,
              backgroundColor: backColor,
              paddingRight: 10,
              '&:before': { backgroundColor: backColor, borderColor: appColors.borderColor },
            }}>
            <Checkbox
              classes={{ indeterminate: 'indeterminate' }}
              checked={isAllSelected}
              sx={{ color: 'rgb(90, 90, 90)', '&.Mui-checked': { color: 'rgb(90, 90, 90)' } }}
              indeterminate={selected.length > 0 && selected.length < listOptions.length}
            />
            <Typography fontSize={fontSize} fontWeight='bold'>
              Select All
            </Typography>
          </MenuItem>

          {Array.isArray(displayedOptions)
            ? displayedOptions.map((item) => {
                if (typeof item !== 'string') {
                  console.warn('DropDownMultiselect only supports string items in listOptions')
                  return null
                }
                return (
                  <MenuItem
                    id={item}
                    value={item}
                    key={item}
                    style={{
                      margin: 5,
                      fontSize,
                      paddingVertical: 2,
                      paddingHorizontal: 10,
                      borderWidth: 1,
                      height: '32px',
                      color: fontColor || appColors.itemTextColor,
                      borderColor: appColors.borderColor,
                      backgroundColor: backColor,
                      paddingRight: 10,
                      '&:before': { backgroundColor: backColor, borderColor: appColors.borderColor },
                    }}>
                    <Checkbox
                      checked={selected.indexOf(item) > -1}
                      sx={{ color: 'rgb(90, 90, 90)', '&.Mui-checked': { color: 'green' } }}
                    />
                    {item}
                  </MenuItem>
                )
              })
            : null}
        </MuiSelect>
      </FormControl>
    </React.Fragment>
  )
}

function DropDownMultiselectVirtualized({
  selected = [],
  defaultValue,
  displayEmpty,
  listOptions = [],
  onClose,
  onChange,
  label = null,
  height = 40,
  fontStyle,
  width = '250px',
  maxWidth = '250px',
  disabled = false,
}) {
  const { theme, getBackColor, getTextColor } = useInnovaTheme()
  const { fontColor, labelSize, fontSize, backColor } = useColors(fontStyle, getBackColor, getTextColor)

  const [searchText, setSearchText] = React.useState('')

  const displayedOptions = React.useMemo(() => {
    if (!Array.isArray(listOptions)) return []
    if (!searchText) return listOptions
    return listOptions.filter((opt) => containsText(opt, searchText))
  }, [listOptions, searchText])

  const ITEM_HEIGHT = 36
  const VISIBLE_COUNT = Math.min(10, displayedOptions.length)

  const listRef = React.useRef(null)
  const scrollOffsetRef = React.useRef(0)

  const toggleItem = React.useCallback(
    (item) => (e) => {
      e.preventDefault()
      e.stopPropagation()
      const set = new Set(selected)
      if (set.has(item)) set.delete(item)
      else set.add(item)
      onChange(Array.from(set))
    },
    [onChange, selected],
  )

  const VirtualizedMenuList = React.useMemo(
    () =>
      React.forwardRef(function VirtualizedMenuListComp(props, ref) {
        const items = displayedOptions

        const Row = ({ index, style }) => {
          const item = items[index]
          const checked = selected.indexOf(item) > -1
          return (
            <div style={style}>
              <MenuItem
                id={item}
                value={item}
                key={item}
                onClick={toggleItem(item)}
                style={{
                  margin: 5,
                  fontSize,
                  paddingVertical: 2,
                  paddingHorizontal: 10,
                  borderWidth: 1,
                  height: `${ITEM_HEIGHT - 4}px`,
                  color: fontColor || appColors.itemTextColor,
                  borderColor: appColors.borderColor,
                  backgroundColor: backColor,
                  paddingRight: 10,
                  '&:before': { backgroundColor: backColor, borderColor: appColors.borderColor },
                }}>
                <Checkbox checked={checked} sx={{ color: 'rgb(90, 90, 90)', '&.Mui-checked': { color: 'green' } }} />
                {item}
              </MenuItem>
            </div>
          )
        }

        React.useLayoutEffect(() => {
          if (listRef.current && scrollOffsetRef.current) {
            listRef.current.scrollTo(scrollOffsetRef.current)
          }
        })

        const handleUnselectAll = (e) => {
          e.preventDefault()
          e.stopPropagation()
          onChange([])
        }

        return (
          <Box
            ref={ref}
            {...props}
            sx={{
              pb: 0,
              backgroundColor: backColor,
              '& .virtualized-mui-menu': {
                scrollbarWidth: 'thin',
                scrollbarColor: '#A0A0A0 transparent',
              },
            }}>
            <ListSubheader disableSticky sx={{ backgroundColor: backColor }}>
              <TextField
                variant='outlined'
                size='small'
                autoFocus
                placeholder='Type to search...'
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') e.stopPropagation()
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon sx={{ color: fontColor }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  fontSize: fontSize || 12,
                  '& .MuiInputBase-root': { backgroundColor: backColor, color: fontColor, fontSize: fontSize || 12 },
                }}
              />

              {selected?.length > 0 && (
                <MenuItem
                  id='dropdown-picker-unselect-all'
                  component='button'
                  role='button'
                  disabled={selected.length === 0}
                  onClick={handleUnselectAll}
                  style={{
                    fontSize,
                    paddingHorizontal: 10,
                    borderWidth: 1,
                    gap: 10,
                    height: '32px',
                    display: 'flex',
                    alignItems: 'end',
                    justifyContent: 'center',
                    color: fontColor || appColors.itemTextColor,
                    borderColor: appColors.borderColor,
                    backgroundColor: backColor,
                    paddingRight: 10,
                    cursor: 'pointer',
                    '&:before': { backgroundColor: backColor, borderColor: appColors.borderColor },
                  }}>
                  <Iconify icon='f7:clear' style={{ width: 20, height: 20 }} />
                  <Typography fontSize={fontSize} fontWeight='bold'>
                    Unselect All
                  </Typography>
                </MenuItem>
              )}
            </ListSubheader>

            <FixedSizeList
              ref={listRef}
              className='virtualized-mui-menu'
              height={VISIBLE_COUNT * ITEM_HEIGHT}
              itemSize={ITEM_HEIGHT}
              itemCount={items.length}
              width={'100%'}
              overscanCount={8}
              initialScrollOffset={scrollOffsetRef.current}
              onScroll={({ scrollOffset }) => {
                scrollOffsetRef.current = scrollOffset
              }}
              style={{ backgroundColor: backColor }}>
              {Row}
            </FixedSizeList>
          </Box>
        )
      }),
    [displayedOptions, backColor, fontColor, fontSize, searchText, selected, toggleItem, VISIBLE_COUNT, onChange],
  )

  const handleChange = (event) => {
    const value = event.target.value
    if (Array.isArray(value)) onChange(value)
  }

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: width }} size={height < 40 ? 'small' : 'medium'}>
        {label !== null ? (
          <InputLabel
            id='dropdown-picker-label'
            sx={{
              color: fontColor,
              fontSize: labelSize,
              '&.Mui-focused': {
                color: theme === 'dark' ? null : fontColor,
                textShadow: theme === 'dark' ? null : `0 0 5px #fff`,
              },
            }}>
            {label}
          </InputLabel>
        ) : null}

        <MuiSelect
          id='dropdown-picker-multiselect-virt'
          labelId={`dropdown-picker-multiselect-virt-label`}
          label={label}
          disabled={disabled}
          value={selected || ''}
          defaultValue={defaultValue}
          displayEmpty={displayEmpty}
          multiple
          renderValue={(vals) => (Array.isArray(vals) ? vals.join(', ') : '')}
          onClose={(e) => {
            setSearchText('')
            onClose && onClose(e)
          }}
          onChange={handleChange}
          style={{
            width,
            maxWidth,
            margin: 0,
            borderRadius: 0,
            color: fontColor,
            fontSize,
            border: '',
            height: `${height}px`,
            boxSizing: 'border-box',
            backgroundColor: backColor,
          }}
          MenuProps={{
            autoFocus: false,
            PaperProps: MenuPaperStyles(backColor, fontSize),
            MenuListProps: {
              disablePadding: true,
              component: VirtualizedMenuList,
            },
          }}
          variant='outlined'>
          []
        </MuiSelect>
      </FormControl>
    </React.Fragment>
  )
}

export default DropDownPicker
